/**
 * The "texts on each drop" cell of a kind's row: what is asked — ★ proven
 * + ✦ fresh AI, read-only when the kind follows the rest, two small
 * numbers when it has a mix of its own — and, when the pool cannot give every
 * proven text, "→ what really goes out" with an amber ⓘ that says why (user,
 * 13/10/2026: "if we don't have 10 best, it will automatically be AI").
 * The read-only glyphs re-animate on every change; an editable cell keeps
 * its fields (a re-key lost the focus after the first digit).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { ArrowRight, type LucideIcon, Repeat, Sparkles, Star } from 'lucide-react';
import { clampMix, DROP_GEN_TEXTS_PER_DROP_MAX, type DropGenTextMix, sameMix } from '@trafficpulse/shared';

import { HintDot, NumberField } from '@/components/ui';
import { cn } from '@/lib/utils';

import { MINIMAL_FIELD } from '../../../../general/controls/field-style';

interface Props {
  /** The kind's words, for the inputs' labels. */
  kindWords: string;
  /** What is asked for this kind. */
  asked: DropGenTextMix;
  /** What really goes out (the engine's own answer); null while the ranking is read. */
  sends: (DropGenTextMix & { short: number }) | null;
  /** Set when the kind has a mix of its own: the two numbers write it. */
  onSet?: (m: DropGenTextMix) => void;
  /** Phase 38 — the drop keeps the texts of the drop it came from: no mix to show. */
  copy?: boolean;
}

/** The amber ⓘ's words when what goes out differs from what is asked: a short pool, or a 0 + 0 mix. */
export function shortWords(asked: DropGenTextMix, sends: DropGenTextMix & { short: number }): string {
  if (sends.short === 0) return `A drop never goes text-less: asked 0 + 0, it sends ✦ ${sends.ai} fresh AI text${sends.ai === 1 ? '' : 's'}.`;
  const pool = asked.proven - sends.short;
  const have = pool === 0 ? 'no proven text in the pool yet' : `only ${pool} proven text${pool === 1 ? '' : 's'} in the pool`;
  return `Asked: ${asked.proven} proven — ${have}, so the other ${sends.short} become fresh AI texts automatically. Each drop sends ★ ${sends.proven} + ✦ ${sends.ai}.`;
}

const Glyph: FC<{ icon: LucideIcon; tone: string; value: number; muted?: boolean }> = ({ icon: Icon, tone, value, muted }) => (
  <span className={cn('inline-flex items-center gap-0.5 font-mono text-xs font-semibold tabular-nums', muted ? 'text-muted-foreground' : tone)}>
    <Icon className="h-3 w-3" aria-hidden />
    {value}
  </span>
);

const Field: FC<{ icon: LucideIcon; tone: string; value: number; label: string; onChange: (v: number) => void }> = ({ icon: Icon, tone, value, label, onChange }) => (
  <span className={cn('inline-flex items-center gap-1', tone)}>
    <Icon className="h-3 w-3" aria-hidden />
    <NumberField value={value} onChange={onChange} min={0} max={DROP_GEN_TEXTS_PER_DROP_MAX} aria-label={label} className={cn(MINIMAL_FIELD, 'h-7 w-10 text-center text-xs')} />
  </span>
);

export const MixCell: FC<Props> = ({ kindWords, asked, sends, onSet, copy }) => {
  const short = sends !== null && !sameMix(sends, asked);
  if (copy)
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid="mix-cell" title="The hit-again drop sends the texts of the drop that hit — pick new texts to give it ★ proven and ✦ fresh AI texts instead.">
        <Repeat className="h-3 w-3" aria-hidden />
        the texts of the drop that hit
      </span>
    );
  return (
    // An editable cell keeps one key: re-keying rebuilt the fields after each digit ("15" became 1, 21/10/2026).
    <span key={onSet ? 'edit' : `${asked.proven}-${asked.ai}-${sends?.short ?? '-'}`} className="inline-flex items-center gap-1.5 duration-300 animate-in fade-in-0 motion-reduce:animate-none" data-testid="mix-cell">
      {onSet ? (
        <>
          <Field icon={Star} tone="text-success" value={asked.proven} label={`${kindWords}: proven texts per drop`} onChange={(v) => onSet(clampMix({ proven: v, ai: asked.ai }))} />
          <Field icon={Sparkles} tone="text-primary" value={asked.ai} label={`${kindWords}: AI texts per drop`} onChange={(v) => onSet(clampMix({ proven: asked.proven, ai: v }))} />
        </>
      ) : (
        <>
          <Glyph icon={Star} tone="text-success" value={asked.proven} muted={short || asked.proven === 0} />
          <Glyph icon={Sparkles} tone="text-primary" value={asked.ai} muted={short || asked.ai === 0} />
        </>
      )}
      {short ? (
        <span className="inline-flex items-center gap-1.5 pl-0.5" data-testid="mix-sends">
          <ArrowRight className="h-3 w-3 text-muted-foreground" aria-hidden />
          <Glyph icon={Star} tone="text-success" value={sends.proven} />
          <Glyph icon={Sparkles} tone="text-primary" value={sends.ai} />
          <HintDot label={shortWords(asked, sends)} className="border-warning/60 text-warning" />
        </span>
      ) : null}
    </span>
  );
};
